/**
 * Algolia locality vs IP-geo ranking for transactional event search.
 *
 * First request with a location uses optional locality filters. If that returns
 * zero hits while filters already match route defaults, a follow-up search sets
 * `isGeoFallbackAfterEmptyLocalityHits` so ranking uses IP geo instead.
 */

export interface AlgoliaLocalityRankingRawOptionsInput {
  hasLocationOptionalFilter: boolean
  onlineFilterActive: boolean
  /**
   * True after a strict locality search returned no hits — use IP geo ranking
   * instead of locality `optionalFilters`.
   */
  isGeoFallbackAfterEmptyLocalityHits: boolean
  useRegionsOptionalFilter: boolean
  slugForOptionalFilter: string
  /** Mirrors client-only Algolia IP geo (`import.meta.client` in components). */
  enableAroundLatLngViaIP: boolean
}

export type AlgoliaLocalityRankingRawOptions =
  | { optionalFilters: string[] }
  | {
      aroundLatLngViaIP: boolean
      aroundRadius: 'all'
      aroundPrecision: 15000
    }

export const getAlgoliaLocalityRankingRawOptions = (
  input: AlgoliaLocalityRankingRawOptionsInput
): AlgoliaLocalityRankingRawOptions => {
  if (
    input.hasLocationOptionalFilter &&
    !input.onlineFilterActive &&
    !input.isGeoFallbackAfterEmptyLocalityHits
  ) {
    return {
      optionalFilters: [
        input.useRegionsOptionalFilter
          ? `regions: ${input.slugForOptionalFilter}`
          : `city_slug: ${input.slugForOptionalFilter}`,
      ],
    }
  }
  return {
    aroundLatLngViaIP: input.enableAroundLatLngViaIP,
    aroundRadius: 'all',
    aroundPrecision: 15000,
  }
}

export type EmptyAlgoliaSearchRecovery = 'none' | 'reset_filters' | 'geo_retry'

export const classifyEmptyAlgoliaSearchRecovery = (input: {
  hits: unknown[] | undefined
  filtersMatchDefault: boolean
}): EmptyAlgoliaSearchRecovery => {
  const { hits, filtersMatchDefault } = input
  if (hits === undefined || hits.length > 0) return 'none'
  if (!filtersMatchDefault) return 'reset_filters'
  return 'geo_retry'
}
